import { useInView } from '../hooks/useInView';
import operationsIntelligence from '../assets/Operations Intellegence.png';
import './AISection.css';

const AI_CAPABILITIES = [
  { label: 'Overall Operating Posture',          color: 'yellow' },
  { label: 'Dispatch Readiness Signals',          color: 'blue' },
  { label: 'Fleet Readiness Signals',             color: 'blue' },
  { label: 'Demand by Hour of Day',               color: 'green' },
  { label: 'Demand by Day of Week',               color: 'green' },
  { label: 'Hottest Pickup Origins',              color: 'green' },
  { label: 'Active TaxiOps Pulse Signals',        color: 'yellow' },
  { label: 'Overdue Pickup Risk Detection',       color: 'yellow' },
  { label: 'Unassigned Trip Alerts',              color: 'yellow' },
  { label: 'Coverage Pressure Signals',           color: 'blue' },
  { label: 'Stale Driver Location Warnings',      color: 'blue' },
  { label: 'Hours-of-Service Pressure',           color: 'yellow' },
  { label: 'Diagnostic Issue Alerts',             color: 'green' },
  { label: 'Trend Awareness',                     color: 'blue' },
  { label: 'Recommended Operational Actions',     color: 'yellow' },
];

const COLOR_MAP = {
  yellow: { dot: '#f5a623', bg: 'rgba(245,166,35,0.07)',  border: 'rgba(245,166,35,0.18)' },
  blue:   { dot: '#60a5fa', bg: 'rgba(59,130,246,0.07)',  border: 'rgba(59,130,246,0.18)' },
  green:  { dot: '#34d399', bg: 'rgba(16,185,129,0.07)', border: 'rgba(16,185,129,0.18)' },
};

export default function AISection() {
  const [headerRef, headerInView] = useInView();
  const [contentRef, contentInView] = useInView();
  const [screenshotRef, screenshotInView] = useInView();

  return (
    <section className="ai-section" id="ai" aria-labelledby="ai-heading">
      <div className="ai-section__bg-glow" aria-hidden="true" />
      <div className="container">
        {/* Header */}
        <div className="ai-section__header" ref={headerRef}>
          <p className={`section-label fade-up ${headerInView ? 'in-view' : ''}`}>
            Operations Intelligence
          </p>
          <h2
            id="ai-heading"
            className={`ai-section__heading fade-up d1 ${headerInView ? 'in-view' : ''}`}
          >
            Intelligence That Keeps Dispatch<br />
            <span className="gradient-text">Ahead of the Day</span>
          </h2>
          <p className={`ai-section__sub fade-up d2 ${headerInView ? 'in-view' : ''}`}>
            TaxiOps organizes live dispatch, fleet, demand, Pulse, HOS, diagnostic, and
            trend data into a focused intelligence view. Operators see what needs attention,
            why it matters, and where to act without sorting through disconnected screens.
          </p>
        </div>

        {/* Content grid */}
        <div className="ai-section__content" ref={contentRef}>
          {/* Left: Key points */}
          <div className="ai-section__left">
            <div className={`ai-section__differentiator fade-up d1 ${contentInView ? 'in-view' : ''}`}>
              <div className="ai-section__diff-icon" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M11 2l2.2 5.2L18 9 14 13l.9 5L11 15.5 7.1 18 8 13 4 9l4.8-1.8L11 2z"
                        stroke="#f5a623" strokeWidth="1.4" strokeLinejoin="round"/>
                  <circle cx="11" cy="10.5" r="1.8" fill="#f5a623" opacity=".35"/>
                </svg>
              </div>
              <div>
                <h3 className="ai-section__diff-title">Operating Posture &amp; Decision Support</h3>
                <p className="ai-section__diff-desc">
                  TaxiOps evaluates dispatch readiness, fleet coverage, HOS pressure,
                  diagnostics, upcoming work, trip trends, cancellations, fare activity,
                  and demand patterns - then surfaces a concise operating posture with
                  specific signal details.
                </p>
              </div>
            </div>

            <div className={`ai-section__differentiator fade-up d2 ${contentInView ? 'in-view' : ''}`}>
              <div className="ai-section__diff-icon ai-section__diff-icon--blue" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <circle cx="11" cy="11" r="8.5" stroke="#60a5fa" strokeWidth="1.4"/>
                  <path d="M7 11h8M11 7v8" stroke="#60a5fa" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
              </div>
              <div>
                <h3 className="ai-section__diff-title">Risk Identification</h3>
                <p className="ai-section__diff-desc">
                  Overdue pickup risks, unassigned trips, coverage pressure, stale
                  driver location signals, and HOS pressure are surfaced before they
                  become operational problems — not discovered after the fact.
                </p>
              </div>
            </div>

            <div className={`ai-section__differentiator fade-up d3 ${contentInView ? 'in-view' : ''}`}>
              <div className="ai-section__diff-icon ai-section__diff-icon--green" aria-hidden="true">
                <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
                  <path d="M5 12l4 4 8-8" stroke="#34d399" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"/>
                  <circle cx="11" cy="11" r="8.5" stroke="#34d399" strokeWidth="1.4"/>
                </svg>
              </div>
              <div>
                <h3 className="ai-section__diff-title">Demand &amp; Fleet Awareness</h3>
                <p className="ai-section__diff-desc">
                  Average demand by hour and day, hottest pickup origins, active Pulse
                  signals, and diagnostic issues are brought into the same operational
                  view so dispatch decisions improve as more history is collected.
                </p>
              </div>
            </div>

            <div className={`ai-section__operator-note fade-up d4 ${contentInView ? 'in-view' : ''}`}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                <circle cx="8" cy="8" r="6.5" stroke="currentColor" strokeWidth="1.3"/>
                <path d="M8 5v3.5M8 10.5v.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round"/>
              </svg>
              <p>
                <strong>Operators remain in control.</strong> TaxiOps AI provides operational
                decision support - not autonomous operations or automated dispatch.
                Intelligence surfaces context and recommendations. Operators make decisions.
              </p>
            </div>
          </div>

          {/* Right: Capability list */}
          <div className={`ai-section__right fade-up d2 ${contentInView ? 'in-view' : ''}`}>
            <h3 className="ai-section__cap-heading">What Operations Intelligence Surfaces</h3>
            <ul className="ai-section__cap-list" >
              {AI_CAPABILITIES.map(({ label, color }) => {
                const c = COLOR_MAP[color];
                return (
                  <li
                    key={label}
                    className="ai-section__cap-item"
                    style={{ background: c.bg, border: `1px solid ${c.border}` }}
                  >
                    <span
                      className="ai-section__cap-dot"
                      style={{ background: c.dot }}
                      aria-hidden="true"
                    />
                    {label}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>

        {/* AI Dashboard placeholder */}
        <div
          className={`ai-section__dashboard scale-up d1 ${screenshotInView ? 'in-view' : ''}`}
          ref={screenshotRef}
        >
          <div className="ai-section__dashboard-label">
            <div className="ai-section__dashboard-badge">
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden="true">
                <path d="M6 1l1.3 3.2L10 5.5 7.5 8l.6 3.2L6 9.7 3.9 11.2l.6-3.2L2 5.5l2.7-.3L6 1z"
                      stroke="currentColor" strokeWidth="1.1" strokeLinejoin="round"/>
              </svg>
              Operations Intelligence
            </div>
            <p>Operating posture, demand profiles, risk signals, and recommended actions - all in one view.</p>
          </div>
          <img
            src={operationsIntelligence}
            alt="TaxiOps Operations Intelligence Dashboard"
            className="ai-section__dashboard-img"
          />
        </div>
      </div>
    </section>
  );
}
